<template>
  <div class="drag-drop-container">
    <!-- Main Upload Area -->
     <v-tooltip :text="$t('_SelectFileToUpload')" location="top start">
      <template v-slot:activator="{ props }">
    <div @drop.prevent="onDrop($event)" @dragover.prevent="handleDragOver" @dragenter.prevent="handleDragEnter"
      @dragleave.prevent="handleDragLeave" @click="triggerFileInput" class="upload-zone" v-bind="props" :class="{
        'drag-active': dragover,
        
      }">
      <!-- Upload Icon and Text 'has-files': uploadedFiles.length > 0-->
      <div class="upload-content">
        <v-btn id="proxy-upload-button" variant="elevated" size="large"
          @click.stop="triggerFileInput" class="upload-main-btn">
          <v-img :src="images.uploadImg" alt="Upload" width="24" height="24" class="mr-2" />
          <span>{{ $t('StreamPopupFileHeader') }}</span>
        </v-btn>
        <div class="upload-text mt-5">
          <v-icon size="16" color="grey" class="mr-1">mdi-information-outline</v-icon>
          <span class="upload-subtitle">{{ $t('_SupportedAudioFormats') }} MP3, WAV, M4A, FLAC {{ $t('_maxUploadSizeMB', { size: maxUploadMB }) }}</span>
        </div>
      </div>
    </div>
      </template>
    </v-tooltip>
    <!-- Files List -->
    <div v-if="uploadedFiles.length > 0" class="files-section">
      <div class="files-header">
        <div class="actions-left">
          <v-chip v-if="uploadedFiles.length > 0" variant="tonal" size="small">
            {{ $t('_filesSelectedCount', { count: uploadedFiles.length }) }}
          </v-chip>
        </div>
        <v-btn variant="text" color="error" size="small" @click="clearAllFiles" class="clear-all-btn">
          {{ $t('ClearAll') }}
        </v-btn>
      </div>

      <div class="files-list">
        <v-card v-for="(file, index) in uploadedFiles" :key="file.name + index" variant="outlined" class="file-item">
          <div class="file-content">
            <div class="file-icon">
              <v-img :src="images.musicImg" alt="music" width="24" height="20" />
            </div>

            <div class="file-details">
              <div class="file-name">{{ file.name }}</div>
              <div class="file-meta">
                <span class="file-size">{{ formatFileSize(file.size) }}</span>
              </div>
            </div>

            <div class="file-actions">
              <v-btn @click.stop="removeFile(file.name)" icon variant="text" color="primary" size="small"
                class="remove-btn">
                <v-icon size="18">mdi-close</v-icon>
              </v-btn>
            </div>
          </div>
        </v-card>
      </div>
    </div>

    <!-- Hidden File Input -->
    <input id="multiple-drag-drop-file-input" :aria-label="$t('StreamPopupFileHeader')" type="file" ref="fileInput" style="display: none;" @change="onFileChange" :multiple="multiple"
      accept="audio/*" />
  </div>
</template>

<script>
import uploadImg from "@/assets/images/uploadcolor.png";
import musicImg from "@/assets/images/music-line.png";

// SpeechToText CreateSpeechDocument / UploadSpeechDocument use [RequestSizeLimit(52428800)], which caps the
// whole multipart request (not each file). Above it Kestrel drops the connection (ERR_CONNECTION_RESET) and the
// page never learns why, so the selected total is checked here before anything is sent.
const MAX_UPLOAD_MB = 50;
// Room for multipart boundaries/headers and the other form fields sent in the same request
const MULTIPART_OVERHEAD_BYTES = 64 * 1024;
const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024 - MULTIPART_OVERHEAD_BYTES;

export default {
  name: "MultipleDragAndDrop",
  emits: ["filesUploaded", "dragover", "dragleave", "drop"],
  props: {
    multiple: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dragover: false,
      uploadedFiles: [],
      dragCounter: 0,
      maxUploadMB: MAX_UPLOAD_MB,
      images:{
        uploadImg: uploadImg,
        musicImg: musicImg
      }
    };
  },
  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    handleDragOver() {
      this.dragover = true;
      this.$emit('dragover');
    },

    handleDragEnter() {
      this.dragCounter++;
      this.dragover = true;
      this.$emit('dragover');
    },

    handleDragLeave() {
      this.dragCounter--;
      if (this.dragCounter === 0) {
        this.dragover = false;
        this.$emit('dragleave');
      }
    },

    removeFile(fileName) {
      const index = this.uploadedFiles.findIndex(file => file.name === fileName);
      if (index > -1) {
        this.uploadedFiles.splice(index, 1);
        this.$emit('filesUploaded', this.uploadedFiles);
      }
    },

    clearAllFiles() {
      this.uploadedFiles = [];
      this.$emit('filesUploaded', this.uploadedFiles);
    },

    onFileChange(event) {
      const files = Array.from(event.target.files);
      this.processFiles(files);
      // Reset input value
      event.target.value = '';
    },

    onDrop(e) {
      this.dragover = false;
      this.dragCounter = 0;
      this.$emit('drop');

      const files = Array.from(e.dataTransfer.files);
      this.processFiles(files);
    },

    processFiles(files) {
      if (!this.multiple && files.length > 1) {
        this.$store.dispatch("addNotification", {
          message: this.$t('_onlyOneFileAtATime'),
          colour: "error"
        });
        return;
      }

      const validFiles = [];
      const invalidFiles = [];

      files.forEach(file => {
        if (file.type.startsWith('audio/') || this.isAudioFile(file.name)) {
          validFiles.push(file);
        } else {
          invalidFiles.push(file.name);
        }
      });

      if (invalidFiles.length > 0) {
        this.$store.dispatch("addNotification", {
          message: this.$t('_invalidFileTypesOnlyAudio', { files: invalidFiles.join(', ') }),
          colour: "error"
        });
      }

      if (validFiles.length > 0) {
        // Replace existing files or add to them based on multiple prop
        const nextFiles = this.multiple ? [...this.uploadedFiles, ...validFiles] : validFiles;
        const totalBytes = nextFiles.reduce((sum, file) => sum + file.size, 0);
        // Reject the whole new batch and keep what was already selected
        if (totalBytes > MAX_UPLOAD_BYTES) {
          this.showUploadSizeExceeded(totalBytes);
          return;
        }
        this.uploadedFiles = nextFiles;
        this.$emit('filesUploaded', this.uploadedFiles);
      }
    },

    showUploadSizeExceeded(totalBytes) {
      this.$swal.fire({
        icon: 'warning',
        title: this.$t('fileSizeExceeded'),
        text: this.$t('_uploadTotalSizeExceeded', { max: MAX_UPLOAD_MB, size: this.formatFileSize(totalBytes) }),
        confirmButtonText: this.$t('ok'),
      });
    },

    isAudioFile(fileName) {
      const audioExtensions = ['.mp3', '.wav', '.m4a', '.flac', '.aac', '.ogg', '.wma'];
      const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
      return audioExtensions.includes(extension);
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    getFileExtension(fileName) {
      return fileName.substring(fileName.lastIndexOf('.') + 1).toUpperCase();
    },

    submit() {
      if (this.uploadedFiles.length) {
        this.$emit("filesUploaded", this.uploadedFiles);
      } else {
        this.$store.dispatch("addNotification", {
          message: this.$t('_noFilesToUpload'),
          colour: "error"
        });
      }
    },

    clearFiles() {
      this.uploadedFiles = [];
      this.$emit('filesUploaded', this.uploadedFiles);
    }
  }
};
</script>

<style scoped>
.drag-drop-container {
  width: 100%;
}

/* Upload Zone */
.upload-zone {
  border: 2px dashed #e0e0e0;
  border-radius: 16px;
  background: linear-gradient(135deg, #FFF2F2 0%, #FFFFFF 100%);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  position: relative;
  overflow: hidden;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.upload-zone:hover {
  border-color: #ffdadb;
  background: #FFF2F2;
  box-shadow: 0 8px 25px rgba(25, 118, 210, 0.15);
}

.upload-zone.drag-active {
  border-color: #ffdadb;
  background: #FFF2F2;
  transform: scale(1.02);
  box-shadow: 0 12px 35px rgba(25, 118, 210, 0.2);
}

.upload-zone.has-files {
  border-color: #4caf50;
  background: rgba(76, 175, 80, 0.02);
}

/* Upload Content */
.upload-content {
  text-align: center;
  padding: 2rem;
}

.upload-icon-wrapper {
  margin-bottom: 1rem;
}

.upload-icon {
  transition: all 0.3s ease;
}

.upload-text {
  margin-bottom: 1.5rem;
}

.upload-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #424242;
  margin-bottom: 0.5rem;
  transition: color 0.3s ease;
}

.upload-subtitle {
  font-size: 0.875rem;
  color: #757575;
  margin: 0;
  transition: color 0.3s ease;
}

.drag-active .upload-title {
  color: #1976d2;
}

.drag-active .upload-subtitle {
  color: #1976d2;
}

.file-info {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

/* Files Section */
.files-section {
  background: white!important;
}

.files-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  padding: 0 0.5rem;
  padding-top: 1rem;
}

.files-title {
  display: flex;
  align-items: center;
  font-weight: 600;
  color: #424242;
  font-size: 0.95rem;
}

.clear-all-btn {
  text-transform: none;
  font-weight: 500;
}

.files-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-height: 300px;
  overflow-y: auto;
}

/* Custom scrollbar */
.files-list::-webkit-scrollbar {
  width: 6px;
}

.files-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.files-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.files-list::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

/* File Item */
.file-item {
  border-radius: 12px;
  transition: all 0.2s ease;
  border: 1px solid #e0e0e0;
  background: #FFFFFF;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.file-item:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.file-content {
  display: flex;
  align-items: center;
  padding: 0.7rem;
  gap: 1rem;
  border-radius: 16px;
  box-shadow: 0 -4px 4px 0 rgba(0, 0, 0, 0.06), 0 4px 4px 0 rgba(0, 0, 0, 0.06);
}

.file-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(114, 248, 118, 0.1);
  border-radius: 10px;
  flex-shrink: 0;
}

.file-details {
  flex: 1;
  min-width: 0;
}

.file-name {
  font-weight: 500;
  color: #424242;
  font-size: 0.9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 0.25rem;
}

.file-meta {
  display: flex;
  align-items: center;
}

.file-size {
  font-size: 0.8rem;
  color: #757575;
}

.file-actions {
  display: flex;
  flex-shrink: 0;
}

.remove-btn {
  border-radius: 8px;
}

.remove-btn:hover {
  background: rgba(112, 172, 250, 0.2);
}

.upload-main-btn {
  position: relative;
  z-index: 2;
  border-radius: 12px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0.5px !important;
  padding: 0 24px !important;
  height: 48px !important;
  background-color: transparent !important;
  border: 1px solid rgba(195, 46, 14, 0.8) !important ;
  color:#C32E0E ;
  transition: all 0.3s ease !important;
}

.upload-main-btn:hover {
  box-shadow: 0 6px 20px rgba(195, 46, 14, 0.35) !important;
  transform: translateY(-1px);
}
/* Responsive Design */
@media (max-width: 600px) {
  .upload-content {
    padding: 1.5rem;
  }

  .upload-title {
    font-size: 1.1rem;
  }

  .upload-subtitle {
    font-size: 0.8rem;
  }

  .file-content {
    padding: 0.75rem;
    gap: 0.75rem;
  }

  .file-icon {
    width: 35px;
    height: 35px;
  }

  .files-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
}
</style>
